import { AlertTriangle, ArrowRight, Filter, Info, Link2, Lock, MapPin, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LogbookActor } from './LogbookActor';
import { LogbookObject } from './LogbookObject';
import { LogbookOutcomeBadge } from './LogbookBadges';
import { LogbookServerChip } from './LogbookServerChip';
import { LogbookCopyButton } from './LogbookCopyButton';
import { cn } from '../../utils/cn';
import { formatLocalDateTime, formatServerDateTime } from '../../utils/timestamps';
import {
  METADATA_GROUP_ORDER, actionMeta, dimensionLabel, formatCoordinates, hasKnownTime,
  metadataGroup, metadataLabel, outcomeMeta, teleportCommand,
} from '../../utils/logbook';
import type { LogbookEvent } from '../../types/logbook';
import type { LogbookServerView } from '../../hooks/useLogbookServers';

interface LogbookEventDetailProps {
  event: LogbookEvent;
  server?: LogbookServerView;
  canProtect?: boolean;
  onAddToCase?: (events: LogbookEvent[]) => void;
  onFilterActor?: (event: LogbookEvent) => void;
  onFilterAction?: (event: LogbookEvent) => void;
  onFilterNearby?: (event: LogbookEvent) => void;
  /** Hides the link to the standalone record when this already is that page. */
  hideOpen?: boolean;
  className?: string;
}

const FIELD = 'flex flex-col gap-0.5 min-w-0';
const FIELD_LABEL = 'eyebrow';
const FIELD_VALUE = 'text-sm text-white font-mono tabular-nums break-all';

/**
 * The full evidence record.
 *
 * Reading order follows what decides a case: what was captured, then where and when, then the
 * before and after state, then the limits of the capture itself. Metadata is grouped rather than
 * dumped as JSON, because a moderator needs the state change and the attribution caveats far more
 * often than they need sixteen raw keys in insertion order.
 */
export const LogbookEventDetail = ({
  event, server, canProtect, onAddToCase, onFilterActor, onFilterAction, onFilterNearby, hideOpen, className,
}: LogbookEventDetailProps) => {
  const meta = actionMeta(event.action);
  const outcome = outcomeMeta(event.outcome);
  const known = hasKnownTime(event);
  const when = known ? new Date(event.timestamp) : null;
  const clusterId = server?.cluster ?? null;

  const metadataEntries = Object.entries(event.metadata ?? {});
  const grouped = METADATA_GROUP_ORDER
    .map((group) => ({ group, entries: metadataEntries.filter(([key]) => metadataGroup(key) === group) }))
    .filter((section) => section.entries.length > 0);

  const before = event.metadata?.before_state ?? event.metadata?.before;
  const after = event.metadata?.after_state ?? event.metadata?.after;

  return (
    <div className={cn('space-y-4', className)}>
      {/* What was captured */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Action</span>
          <span className="text-sm text-white">{meta.label}</span>
          <span className="text-xs text-glass-500 font-mono break-all">{event.action}</span>
        </div>
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Outcome</span>
          <span><LogbookOutcomeBadge outcome={event.outcome} /></span>
          <span className="text-xs text-glass-500">{outcome.meaning}</span>
        </div>
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Captured by</span>
          <span className="text-sm text-white">{meta.source}</span>
          <span className="text-xs text-glass-500 font-mono break-all">{event.integration}</span>
        </div>
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Object</span>
          <span className="text-sm"><LogbookObject objectId={event.object_id} clusterId={clusterId} quantity={event.quantity} /></span>
          <span className="text-xs text-glass-500 font-mono break-all">{event.object_id || 'none'}</span>
        </div>
      </div>

      {/* Before and after, when the capture recorded a state change */}
      {(before || after) && (
        <div className="subcard p-3">
          <div className="eyebrow mb-2">State change</div>
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <code className="flex-1 min-w-0 text-xs font-mono break-all rounded-lg px-2.5 py-2 bg-red-500/10 text-red-300 border border-red-500/20">
              {before || 'not recorded'}
            </code>
            <ArrowRight className="w-4 h-4 text-glass-400 shrink-0 mx-auto md:mx-0 rotate-90 md:rotate-0" />
            <code className="flex-1 min-w-0 text-xs font-mono break-all rounded-lg px-2.5 py-2 bg-green-500/10 text-green-300 border border-green-500/20">
              {after || 'not recorded'}
            </code>
          </div>
        </div>
      )}

      {/* Who, where, when */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Actor</span>
          <span className="text-sm"><LogbookActor actorId={event.actor_id} actorName={event.actor_name} actorKind={event.actor_kind} /></span>
          <span className="text-xs text-glass-500 font-mono break-all">{event.actor_id || 'no identity recorded'}</span>
        </div>
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Position</span>
          <span className="flex items-center gap-1.5 text-sm text-white font-mono tabular-nums">
            <MapPin className="w-3.5 h-3.5 text-glass-400 shrink-0" />
            {formatCoordinates(event)}
            <LogbookCopyButton value={teleportCommand(event)} label="Teleport command" />
          </span>
          <span className="text-xs text-glass-500">{dimensionLabel(event.dimension)} &middot; {event.world_id}</span>
        </div>
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Recorded</span>
          <span className="text-sm text-white tabular-nums">{when ? formatLocalDateTime(when) : 'Unknown time'}</span>
          <span className="text-xs text-glass-500 tabular-nums">
            {when ? formatServerDateTime(when) : 'No time was recorded.'}
          </span>
        </div>
      </div>

      {/* Identity of the record itself */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Event id</span>
          <span className="flex items-center gap-1.5">
            <code className={FIELD_VALUE}>{event.event_id}</code>
            <LogbookCopyButton value={event.event_id} label="Event id" />
          </span>
        </div>
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Server</span>
          <span><LogbookServerChip serverId={event.server_id} server={server} detailed /></span>
        </div>
        <div className={FIELD}>
          <span className={FIELD_LABEL}>Producer</span>
          <code className="text-xs text-glass-400 font-mono break-all">{event.producer_id} #{event.sequence}</code>
        </div>
      </div>

      {/* Remaining metadata, grouped so the caveats land last */}
      {grouped.length > 0 && (
        <div className="space-y-3">
          {grouped.map(({ group, entries }) => (
            <div key={group} className="subcard p-3">
              <div className="eyebrow mb-2 flex items-center gap-1.5">
                {group === 'Evidence limits' && <Info className="w-3 h-3" />}
                {group}
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {entries.map(([key, value]) => (
                  <div key={key} className="min-w-0">
                    <dt className="text-xs text-glass-500">{metadataLabel(key)}</dt>
                    <dd className="text-sm text-glass-200 font-mono break-all">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}

      {/* What this record does not establish. The server's own evidence_limit is specific to the
          record and is already shown above, so Argus's general note only fills in when absent. */}
      {meta.caveat && !event.metadata?.evidence_limit && (
        <div className="alert alert-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{meta.caveat}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!hideOpen && (
          <Link to={`/logbook/event/${encodeURIComponent(event.event_id)}`} className="glass-button-sm">
            <Link2 className="w-4 h-4" />
            Open record
          </Link>
        )}
        {canProtect && onAddToCase && (
          <button type="button" className="glass-button-sm" onClick={() => onAddToCase([event])}>
            <Lock className="w-4 h-4" />
            Add to a case
          </button>
        )}
        {onFilterActor && event.actor_id && (
          <button type="button" className="btn-ghost btn-xs" onClick={() => onFilterActor(event)}>
            <User className="w-3.5 h-3.5" />
            Only this actor
          </button>
        )}
        {onFilterAction && (
          <button type="button" className="btn-ghost btn-xs" onClick={() => onFilterAction(event)}>
            <Filter className="w-3.5 h-3.5" />
            Only this action
          </button>
        )}
        {onFilterNearby && (
          <button type="button" className="btn-ghost btn-xs" onClick={() => onFilterNearby(event)}>
            <MapPin className="w-3.5 h-3.5" />
            Everything nearby
          </button>
        )}
      </div>
    </div>
  );
};
